import { type ReactNode, type RefObject, useEffect, useRef, useState } from "react";
import { CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { refForCell } from "@/zapi-frontend/shared/lib/figureRefs";
import type { TFigureRef } from "@/zapi-frontend/shared/types/contentBlocks";
import { RecordChip } from "./_RecordChip";
import { FigureTrigger } from "./FigureRefs/FigureTrigger";
import { memoType } from "./memoTypography";

const FIGURE_CELL = /^[+\-−]?[$€£¥]?\s?\d[\d,.]*\s?(%|x|×|pp|bps|[a-z]{1,3})?$/i;
const EMPTY_CELL = /^(|—|–|-|n\/a)$/i;
const TOTAL_ROW = /^total\b/i;
const SCROLL_STEP_RATIO = 0.8;

type TColumnKind = "name" | "badge" | "figure" | "text";

const CELL_CLASSES: Record<TColumnKind, string> = {
  name: "font-medium text-black",
  badge: "text-innoveraZinc-900",
  figure: "text-innoveraZinc-900 whitespace-nowrap text-right tabular-nums",
  text: "text-innoveraZinc-900"
};

const MIN_WIDTH_CLASSES: Record<TColumnKind, string> = {
  name: "min-w-[110px]",
  badge: "min-w-[120px]",
  figure: "min-w-[64px]",
  text: "min-w-[152px]"
};

// Three or more prose columns get room to read and the table scrolls sideways instead.
const WIDE_TEXT_MIN_WIDTH = "min-w-[240px]";

// 12 px at the row's edges and 16 px between cells, as the design lays the columns out.
const cellPadding = (column: number, count: number) =>
  column === 0 ? "pl-3 pr-2" : column === count - 1 ? "pl-2 pr-3" : "px-2";

// Columns are addressed by index, not by name: two columns can share a header and the column
// above row labels usually has none, so a name cannot identify one.
const columnKinds = (cols: string[], rows: string[][], badgeCols: number[]): TColumnKind[] =>
  cols.map((_col, c) => {
    if (c === 0) return "name";
    if (badgeCols.includes(c)) return "badge";
    const cells = rows.map((row) => (row[c] ?? "").trim()).filter((cell) => !EMPTY_CELL.test(cell));
    return cells.length > 0 && cells.every((cell) => FIGURE_CELL.test(cell)) ? "figure" : "text";
  });

// Standalone table widgets set figures in JetBrains Mono; a chart's "The Numbers" table keeps
// the memo's Inter, as in the design. A grid's first column names the rows, so its header
// stays blank there.
type TBlockTableProps = {
  cols: string[];
  rows: string[][];
  badgeCols?: number[];
  monoFigures?: boolean;
  hideFirstHeader?: boolean;
  refs?: TFigureRef[];
};

export function BlockTable({ cols, rows, badgeCols = [], monoFigures = true, hideFirstHeader, refs }: TBlockTableProps) {
  const kinds = columnKinds(cols, rows, badgeCols);
  const textColumns = kinds.filter((kind) => kind === "text").length;
  const minWidth = (kind: TColumnKind) =>
    kind === "text" && textColumns >= 3 ? WIDE_TEXT_MIN_WIDTH : MIN_WIDTH_CLASSES[kind];

  return (
    <ScrollableTable>
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {cols.map((col, c) => (
              <th
                key={c}
                className={cn(
                  "border-b border-innoveraZinc-150 py-3 text-left",
                  memoType.overline,
                  cellPadding(c, cols.length),
                  kinds[c] === "figure" && "text-right"
                )}
              >
                {kinds[c] === "badge" || (c === 0 && hideFirstHeader) ? (
                  <span className="sr-only">{col}</span>
                ) : (
                  <div className={minWidth(kinds[c])}>{col}</div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => (
            <tr
              key={r}
              className={cn(
                "border-b border-innoveraZinc-150 last:border-b-0",
                TOTAL_ROW.test(row[0] ?? "") && "bg-innoveraZinc-100"
              )}
            >
              {cols.map((_col, c) => (
                <td
                  key={c}
                  className={cn(
                    "py-4 align-top",
                    memoType.tableCell,
                    cellPadding(c, cols.length),
                    CELL_CLASSES[kinds[c]],
                    kinds[c] === "figure" && monoFigures && memoType.number
                  )}
                >
                  <div className={minWidth(kinds[c])}>
                    {kinds[c] === "badge" && row[c] ? (
                      <RecordChip value={row[c]} />
                    ) : row[c] ? (
                      <FigureTrigger figureKey={refForCell(refs, r, c)?.key}>{row[c]}</FigureTrigger>
                    ) : (
                      <span className={cn(monoFigures && memoType.number)}>—</span>
                    )}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </ScrollableTable>
  );
}

function useScrollEdges(ref: RefObject<HTMLDivElement>) {
  const [edges, setEdges] = useState({ left: false, right: false });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const left = el.scrollLeft > 1;
      const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setEdges((previous) => (previous.left === left && previous.right === right ? previous : { left, right }));
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => {
      el.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [ref]);

  return edges;
}

// A table wider than the memo scrolls sideways; a fade and a round arrow mark each hidden edge.
// The scrolling box is positioned so the hidden (absolutely placed) header labels stay clipped
// inside it instead of widening the chapter.
function ScrollableTable({ children }: { children: ReactNode }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const edges = useScrollEdges(scrollRef);
  const scrollByStep = (direction: 1 | -1) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: direction * el.clientWidth * SCROLL_STEP_RATIO, behavior: "smooth" });
  };

  return (
    <div className="relative">
      <div ref={scrollRef} className="relative overflow-x-auto">
        {children}
      </div>
      <ScrollEdge side="left" visible={edges.left} onClick={() => scrollByStep(-1)} />
      <ScrollEdge side="right" visible={edges.right} onClick={() => scrollByStep(1)} />
    </div>
  );
}

function ScrollEdge({ side, visible, onClick }: { side: "left" | "right"; visible: boolean; onClick: () => void }) {
  const Icon = side === "left" ? CaretLeftIcon : CaretRightIcon;
  return (
    <>
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-y-0 w-[60px] from-white to-transparent transition-opacity",
          side === "left" ? "left-0 bg-linear-to-r" : "right-0 bg-linear-to-l",
          visible ? "opacity-100" : "opacity-0"
        )}
      />
      <button
        type="button"
        aria-label={side === "left" ? "Scroll table left" : "Scroll table right"}
        onClick={onClick}
        className={cn(
          "absolute top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-full border border-innoveraZinc-150 bg-white text-innoveraZinc-700 shadow-[0_2px_8px_rgba(0,0,0,0.07)] transition-opacity",
          side === "left" ? "left-1.5" : "right-1.5",
          visible ? "opacity-100" : "pointer-events-none opacity-0"
        )}
      >
        <Icon className="size-4" />
      </button>
    </>
  );
}
